import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../AuthContext";
import { GITLAB_API_URL, GITLAB_BASE_URL } from "../constants";

const REQUEST_TIMEOUT_MS = 30000;
const PAGE_SIZE = 100;
const CACHE_TTL_MS = 10 * 60 * 1000;
const MAX_CACHE_ENTRIES = 30;
const CACHE_STORAGE_KEY = "gitlab-diff-cache-v3";
const MAX_EXPANDABLE_FILE_CONTENT_BYTES = 1_000_000;
const SPLIT_SIDE = Object.freeze({
	OLD: 1,
	NEW: 2,
});

const diffCache = new Map();
let diffCacheHydrated = false;

export const clearMergeRequestDiffCache = () => {
	diffCache.clear();
	diffCacheHydrated = true;
	try {
		sessionStorage.removeItem(CACHE_STORAGE_KEY);
	} catch (error) {
		console.error("Failed to clear diff cache:", error);
	}
};

const getCacheKey = ({ projectId, iid, user }) =>
	projectId && iid
		? `${user?.profile?.sub || user?.profile?.preferred_username || "user"}:${projectId}:${iid}`
		: "";

const isCacheEntryFresh = (entry) =>
	entry && Date.now() - entry.cachedAt < CACHE_TTL_MS;

const readStoredDiffCache = () => {
	try {
		const rawValue = sessionStorage.getItem(CACHE_STORAGE_KEY);
		const parsed = rawValue ? JSON.parse(rawValue) : [];
		return Array.isArray(parsed) ? parsed : [];
	} catch (error) {
		console.error("Failed to read diff cache:", error);
		return [];
	}
};

const writeStoredDiffCache = () => {
	try {
		const entries = [...diffCache.entries()]
			.filter(([, entry]) => isCacheEntryFresh(entry))
			.slice(-MAX_CACHE_ENTRIES);
		sessionStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(entries));
	} catch (error) {
		console.error("Failed to write diff cache:", error);
	}
};

const hydrateDiffCache = () => {
	if (diffCacheHydrated) return;
	diffCacheHydrated = true;

	for (const [key, entry] of readStoredDiffCache()) {
		if (typeof key === "string" && isCacheEntryFresh(entry)) {
			diffCache.set(key, entry);
		}
	}
};

const getCachedDiffState = (cacheKey) => {
	if (!cacheKey) return null;

	hydrateDiffCache();
	const entry = diffCache.get(cacheKey);
	if (!isCacheEntryFresh(entry)) {
		if (entry) diffCache.delete(cacheKey);
		writeStoredDiffCache();
		return null;
	}

	diffCache.delete(cacheKey);
	diffCache.set(cacheKey, entry);
	return entry.state;
};

const trimDiffCache = () => {
	while (diffCache.size > MAX_CACHE_ENTRIES) {
		const oldestKey = diffCache.keys().next().value;
		if (!oldestKey) return;
		diffCache.delete(oldestKey);
	}
};

const setCachedDiffState = (cacheKey, state) => {
	if (!cacheKey) return;

	hydrateDiffCache();
	diffCache.set(cacheKey, {
		cachedAt: Date.now(),
		state,
	});
	trimDiffCache();
	writeStoredDiffCache();
};

const deleteCachedDiffState = (cacheKey) => {
	if (!cacheKey) return;

	hydrateDiffCache();
	diffCache.delete(cacheKey);
	writeStoredDiffCache();
};

const initialState = {
	files: [],
	diffRefs: null,
	discussions: [],
	loading: false,
	commentsLoading: false,
	error: null,
	commentsError: null,
	loaded: false,
};

const initialApprovalState = {
	loading: false,
	error: null,
	approvalsLeft: null,
	approvalsRequired: null,
	approvedBy: [],
	currentUserApproved: null,
	loaded: false,
};

const getProjectPath = (mr) =>
	mr?.project?.fullPath ||
	mr?.projectPath ||
	mr?.reference?.split("!")[0] ||
	"";

const encodeProjectPath = (projectPath) => encodeURIComponent(projectPath);

const normalizeLineEndings = (value = "") => value.replace(/\r\n?/g, "\n");

const jsonOrText = async (response) => {
	const text = await response.text();
	if (!text) return null;

	try {
		return JSON.parse(text);
	} catch {
		return text;
	}
};

const formatGitLabError = (payload, status) => {
	if (payload?.message) {
		if (typeof payload.message === "string") return payload.message;
		if (typeof payload.message === "object") {
			return Object.entries(payload.message)
				.map(
					([key, value]) =>
						`${key} ${Array.isArray(value) ? value.join(", ") : value}`,
				)
				.join("; ");
		}
	}
	if (typeof payload === "string" && payload.trim()) return payload;
	return `GitLab request failed (${status})`;
};

const gitlabRequest = async (
	path,
	{ token, method = "GET", body, signal } = {},
) => {
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
	const abortFromParent = () => controller.abort();

	if (signal) {
		if (signal.aborted) controller.abort();
		signal.addEventListener("abort", abortFromParent, { once: true });
	}

	try {
		const response = await fetch(`${GITLAB_API_URL}${path}`, {
			method,
			headers: {
				Accept: "application/json",
				...(body ? { "Content-Type": "application/json" } : {}),
				...(token ? { Authorization: `Bearer ${token}` } : {}),
			},
			body: body ? JSON.stringify(body) : undefined,
			signal: controller.signal,
		});
		const payload = await jsonOrText(response);

		if (!response.ok) {
			const error = new Error(formatGitLabError(payload, response.status));
			error.status = response.status;
			throw error;
		}

		return { data: payload, headers: response.headers };
	} catch (error) {
		if (error.name === "AbortError") {
			throw new Error("GitLab request timed out");
		}
		throw error;
	} finally {
		clearTimeout(timeoutId);
		if (signal) signal.removeEventListener("abort", abortFromParent);
	}
};

const fetchPaginatedArray = async (path, options) => {
	const items = [];
	let page = 1;
	let nextPage = "1";

	while (nextPage) {
		const separator = path.includes("?") ? "&" : "?";
		const pagePath = `${path}${separator}per_page=${PAGE_SIZE}&page=${page}`;
		const { data, headers } = await gitlabRequest(pagePath, options);
		if (!Array.isArray(data)) {
			throw new Error("GitLab returned an unexpected response for the diff");
		}
		items.push(...data);
		nextPage = headers.get("x-next-page");
		page = Number(nextPage || 0);
	}

	return items;
};

const fetchDiffFiles = async ({ projectId, iid, token, signal }) => {
	const diffsPath = `/projects/${projectId}/merge_requests/${iid}/diffs?unidiff=true`;

	try {
		return await fetchPaginatedArray(diffsPath, { token, signal });
	} catch (error) {
		if (![400, 404].includes(error.status)) throw error;
	}

	const changesPath = `/projects/${projectId}/merge_requests/${iid}/changes?unidiff=true`;
	const { data } = await gitlabRequest(changesPath, { token, signal });
	return Array.isArray(data?.changes) ? data.changes : [];
};

const normalizeDiffRefs = (value) => {
	if (!value) return null;

	const refs = value.diff_refs || value;
	const baseSha = refs.base_sha || refs.base_commit_sha;
	const startSha = refs.start_sha || refs.start_commit_sha;
	const headSha = refs.head_sha || refs.head_commit_sha;

	if (!baseSha || !startSha || !headSha) return null;
	return { baseSha, startSha, headSha };
};

const fetchDiffRefs = async ({ projectId, iid, token, signal }) => {
	const versions = await fetchPaginatedArray(
		`/projects/${projectId}/merge_requests/${iid}/versions`,
		{ token, signal },
	);
	const latestVersion = versions
		.map((version) => ({
			...version,
			_refs: normalizeDiffRefs(version),
		}))
		.filter((version) => version._refs)
		.sort((a, b) => {
			const aDate = new Date(a.created_at || a.createdAt || 0).getTime();
			const bDate = new Date(b.created_at || b.createdAt || 0).getTime();
			return bDate - aDate || Number(b.id || 0) - Number(a.id || 0);
		})[0];

	if (latestVersion?._refs) return latestVersion._refs;

	const { data } = await gitlabRequest(
		`/projects/${projectId}/merge_requests/${iid}`,
		{ token, signal },
	);
	return normalizeDiffRefs(data);
};

const fetchRawFileContent = async ({
	projectId,
	filePath,
	ref,
	token,
	signal,
}) => {
	if (!projectId || !filePath || !ref) return "";

	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
	const abortFromParent = () => controller.abort();

	if (signal) {
		if (signal.aborted) controller.abort();
		signal.addEventListener("abort", abortFromParent, { once: true });
	}

	try {
		const response = await fetch(
			`${GITLAB_API_URL}/projects/${projectId}/repository/files/${encodeURIComponent(filePath)}/raw?ref=${encodeURIComponent(ref)}`,
			{
				headers: {
					Accept: "text/plain, */*",
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
				signal: controller.signal,
			},
		);

		if (!response.ok) {
			const payload = await jsonOrText(response);
			throw new Error(formatGitLabError(payload, response.status));
		}

		const contentLength = Number(response.headers.get("Content-Length"));
		if (
			Number.isFinite(contentLength) &&
			contentLength > MAX_EXPANDABLE_FILE_CONTENT_BYTES
		) {
			throw new Error("File is too large to expand inline");
		}

		const content = await response.text();
		if (content.length > MAX_EXPANDABLE_FILE_CONTENT_BYTES) {
			throw new Error("File is too large to expand inline");
		}

		return normalizeLineEndings(content);
	} catch (error) {
		if (error.name === "AbortError") {
			throw new Error("GitLab request timed out");
		}
		throw error;
	} finally {
		clearTimeout(timeoutId);
		if (signal) signal.removeEventListener("abort", abortFromParent);
	}
};

const withGitLabBaseUrl = (url) => {
	if (!url) return "";
	return url.startsWith("http://") || url.startsWith("https://")
		? url
		: `${GITLAB_BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

const rewriteRelativeUrls = (html) => {
	if (!html) return "";
	return html
		.replace(/(href|src)="\/(?!\/)/g, `$1="${GITLAB_BASE_URL}/`)
		.replace(/(href|src)='\/(?!\/)/g, `$1='${GITLAB_BASE_URL}/`);
};

const normalizeAuthor = (author = {}) => ({
	id: author.id,
	name: author.name || author.username || "Unknown user",
	username: author.username || "",
	avatarUrl: withGitLabBaseUrl(author.avatar_url || author.avatarUrl || ""),
	webUrl: withGitLabBaseUrl(author.web_url || author.webUrl || ""),
});

const normalizeAwardEmojis = (awardEmojis = [], currentUsername = "") => {
	const grouped = new Map();

	for (const award of awardEmojis || []) {
		const name = award?.name;
		if (!name) continue;

		const existing = grouped.get(name) || {
			name,
			count: 0,
			currentUserReacted: false,
		};
		existing.count += Number(award?.count) || 1;
		existing.currentUserReacted =
			existing.currentUserReacted ||
			Boolean(award?.currentUserReacted) ||
			(Boolean(currentUsername) && award?.user?.username === currentUsername);
		grouped.set(name, existing);
	}

	return [...grouped.values()].sort((a, b) => a.name.localeCompare(b.name));
};

const getUserIdentityValues = (user) => {
	const profile = user?.profile || {};
	return new Set(
		[
			profile.gitlab_id,
			profile.id,
			profile.sub,
			profile.username,
			profile.preferred_username,
			profile.nickname,
			profile.name,
			profile.email?.split("@")[0],
		]
			.filter(Boolean)
			.map((value) => String(value).toLowerCase()),
	);
};

const approvalUserMatchesCurrentUser = (approvalUser, currentUser) => {
	const identityValues = getUserIdentityValues(currentUser);
	if (identityValues.size === 0 || !approvalUser) return false;

	return [
		approvalUser.id,
		approvalUser.username,
		approvalUser.name,
		approvalUser.web_url?.split("/").pop(),
		approvalUser.webUrl?.split("/").pop(),
	]
		.filter(Boolean)
		.some((value) => identityValues.has(String(value).toLowerCase()));
};

const normalizeApprovalState = (payload, currentUser, fallback = {}) => {
	const source = payload || {};
	const approvedByEntries = Array.isArray(source.approved_by)
		? source.approved_by
		: [];
	const approvedBy = approvedByEntries.map((entry) =>
		normalizeAuthor(entry.user || entry),
	);
	const matchedCurrentUser = approvedByEntries.some((entry) =>
		approvalUserMatchesCurrentUser(entry.user || entry, currentUser),
	);
	const approvalsLeft = Number(source.approvals_left);
	const approvalsRequired = Number(source.approvals_required);

	return {
		loading: false,
		error: null,
		approvalsLeft: Number.isFinite(approvalsLeft) ? approvalsLeft : null,
		approvalsRequired: Number.isFinite(approvalsRequired)
			? approvalsRequired
			: null,
		approvedBy,
		currentUserApproved:
			matchedCurrentUser || fallback.currentUserApproved || false,
		loaded: true,
	};
};

const getInitialApprovalState = (mr) => ({
	...initialApprovalState,
	approvalsLeft:
		typeof mr?.approvalsLeft === "number"
			? Math.max(0, mr.approvalsLeft)
			: null,
	approvedBy: Array.isArray(mr?.approvers) ? mr.approvers : [],
	currentUserApproved: mr?.myReview === "APPROVED",
	loaded: Boolean(mr?.approvers || typeof mr?.approvalsLeft === "number"),
});

const normalizeNote = (discussionId, note = {}, currentUsername = "") => ({
	id: note.id,
	restId: note.id,
	discussionId,
	body: note.body || "",
	bodyHtml: rewriteRelativeUrls(note.body_html || note.bodyHtml || ""),
	author: normalizeAuthor(note.author),
	createdAt: note.created_at || note.createdAt || "",
	updatedAt: note.updated_at || note.updatedAt || "",
	system: Boolean(note.system),
	type: note.type || "",
	resolvable: Boolean(note.resolvable),
	resolved: Boolean(note.resolved),
	position: note.position || null,
	awardEmojis: normalizeAwardEmojis(
		note.award_emoji || note.awardEmoji || note.awardEmojis || [],
		currentUsername,
	),
});

const getNoteUrl = (note, mrWebUrl = "") =>
	note?.url ||
	note?.web_url ||
	(note?.id && mrWebUrl ? `${mrWebUrl}#note_${note.id}` : "");

const getDiscussionPosition = (discussion = {}) =>
	(discussion.notes || []).find((note) => note.position)?.position || null;

const normalizeDiscussion = (
	discussion = {},
	currentUsername = "",
	mrWebUrl = "",
) => {
	const position = getDiscussionPosition(discussion);
	if (!position) return null;

	const notes = (discussion.notes || [])
		.map((note) => normalizeNote(discussion.id, note, currentUsername))
		.map((note) => ({
			...note,
			url: getNoteUrl(
				(discussion.notes || []).find((rawNote) => rawNote.id === note.id),
				mrWebUrl,
			),
		}))
		.filter((note) => !note.system && note.body);
	if (notes.length === 0) return null;

	const newLine = Number(position.new_line || position.newLine || 0);
	const oldLine = Number(position.old_line || position.oldLine || 0);
	const side = newLine > 0 ? "new" : "old";
	const lineNumber = side === "new" ? newLine : oldLine;
	if (!lineNumber) return null;

	return {
		id: discussion.id,
		individualNote: Boolean(
			discussion.individual_note || discussion.individualNote,
		),
		position,
		oldPath: position.old_path || position.oldPath || "",
		newPath: position.new_path || position.newPath || "",
		oldLine: oldLine || null,
		newLine: newLine || null,
		side,
		lineNumber,
		notes,
	};
};

const fetchDiscussionAwardEmojiMap = async ({
	discussions,
	projectId,
	iid,
	token,
	currentUsername,
	signal,
}) => {
	const noteIds = [
		...new Set(
			(discussions || [])
				.flatMap((discussion) => discussion.notes || [])
				.map((note) => note.restId || note.id)
				.filter(Boolean),
		),
	];

	if (noteIds.length === 0) return new Map();

	return new Map(
		await Promise.all(
			noteIds.map(async (noteId) => {
				try {
					const { data } = await gitlabRequest(
						`/projects/${projectId}/merge_requests/${iid}/notes/${encodeURIComponent(noteId)}/award_emoji`,
						{ token, signal },
					);
					return [noteId, normalizeAwardEmojis(data || [], currentUsername)];
				} catch (error) {
					if (error.message === "GitLab request timed out") throw error;
					console.warn("Failed to load inline comment emoji reactions:", error);
					return [noteId, []];
				}
			}),
		),
	);
};

const applyAwardEmojisToDiscussions = (discussions, awardsByNoteId) =>
	(discussions || []).map((discussion) => ({
		...discussion,
		notes: (discussion.notes || []).map((note) => ({
			...note,
			awardEmojis:
				awardsByNoteId.get(note.restId || note.id) || note.awardEmojis || [],
		})),
	}));

const fetchDiscussions = async ({
	projectId,
	iid,
	token,
	signal,
	currentUsername = "",
	mrWebUrl = "",
}) => {
	const discussions = await fetchPaginatedArray(
		`/projects/${projectId}/merge_requests/${iid}/discussions?render_html=true`,
		{ token, signal },
	);

	const normalizedDiscussions = discussions
		.map((discussion) =>
			normalizeDiscussion(discussion, currentUsername, mrWebUrl),
		)
		.filter(Boolean);
	const awardsByNoteId = await fetchDiscussionAwardEmojiMap({
		discussions: normalizedDiscussions,
		projectId,
		iid,
		token,
		currentUsername,
		signal,
	});

	return applyAwardEmojisToDiscussions(normalizedDiscussions, awardsByNoteId);
};

const fetchApprovalState = async ({ projectId, iid, token, signal }) => {
	const { data } = await gitlabRequest(
		`/projects/${projectId}/merge_requests/${iid}/approvals`,
		{ token, signal },
	);
	return data || {};
};

const getPathForDisplay = (change) =>
	change.new_path || change.newPath || change.old_path || change.oldPath || "";

const getLangFromPath = (path) => {
	const extension = path.split(".").pop()?.toLowerCase();
	const aliases = {
		cjs: "javascript",
		css: "css",
		cts: "typescript",
		go: "go",
		htm: "html",
		html: "html",
		js: "javascript",
		json: "json",
		jsx: "javascript",
		md: "markdown",
		mjs: "javascript",
		mts: "typescript",
		py: "python",
		rb: "ruby",
		rs: "rust",
		scss: "scss",
		sh: "bash",
		ts: "typescript",
		tsx: "typescript",
		yaml: "yaml",
		yml: "yaml",
	};
	return aliases[extension] || "txt";
};

const getDiffPath = (path, prefix) => {
	if (!path || path === "/dev/null") return "/dev/null";
	return path.startsWith(`${prefix}/`) ? path : `${prefix}/${path}`;
};

const buildUnifiedDiff = (change, diff, oldPath, newPath) => {
	const normalized = normalizeLineEndings(diff).trimEnd();
	if (!normalized) return [];

	if (/^diff --git /m.test(normalized) || /^--- /m.test(normalized)) {
		return [normalized];
	}

	const isNewFile = Boolean(change.new_file || change.newFile);
	const isDeletedFile = Boolean(change.deleted_file || change.deletedFile);
	const oldHeaderPath = isNewFile ? "/dev/null" : getDiffPath(oldPath, "a");
	const newHeaderPath = isDeletedFile ? "/dev/null" : getDiffPath(newPath, "b");
	const lines = [
		`diff --git ${getDiffPath(oldPath, "a")} ${getDiffPath(newPath, "b")}`,
		`--- ${oldHeaderPath}`,
		`+++ ${newHeaderPath}`,
		normalized,
	];

	return [lines.join("\n")];
};

const getDiffBodies = (diff) => {
	const normalized = normalizeLineEndings(diff).trimEnd();
	if (!normalized) return [];

	const firstHunkIndex = normalized.search(/^@@ /m);
	const hunkBody = firstHunkIndex >= 0 ? normalized.slice(firstHunkIndex) : "";
	if (!hunkBody) return [];

	return hunkBody
		.split(/(?=^@@ )/m)
		.map((hunk) => hunk.trimEnd())
		.filter((hunk) => hunk.startsWith("@@"));
};

const parseHunkHeader = (line) => {
	const match = line.match(/^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/);
	if (!match) return null;
	return {
		oldLine: Number(match[1]),
		newLine: Number(match[2]),
	};
};

const parseDiffRows = (diff) => {
	const rows = [];
	let oldLine = 0;
	let newLine = 0;
	let inHunk = false;

	for (const rawLine of normalizeLineEndings(diff).split("\n")) {
		const hunk = parseHunkHeader(rawLine);
		if (hunk) {
			oldLine = hunk.oldLine;
			newLine = hunk.newLine;
			inHunk = true;
			continue;
		}

		if (!inHunk || rawLine.startsWith("\\ No newline")) continue;

		if (rawLine.startsWith("+")) {
			rows.push({ type: "add", oldLine: null, newLine });
			newLine += 1;
			continue;
		}

		if (rawLine.startsWith("-")) {
			rows.push({ type: "delete", oldLine, newLine: null });
			oldLine += 1;
			continue;
		}

		rows.push({ type: "context", oldLine, newLine });
		oldLine += 1;
		newLine += 1;
	}

	return rows;
};

const countDiffStats = (diff) => {
	let additions = 0;
	let deletions = 0;

	for (const rawLine of normalizeLineEndings(diff).split("\n")) {
		if (rawLine.startsWith("+++") || rawLine.startsWith("---")) continue;
		if (rawLine.startsWith("+")) additions += 1;
		if (rawLine.startsWith("-")) deletions += 1;
	}

	return {
		additions,
		deletions,
	};
};

const normalizeDiffFile = (change, index) => {
	const oldPath =
		change.old_path || change.oldPath || getPathForDisplay(change);
	const newPath =
		change.new_path || change.newPath || getPathForDisplay(change);
	const path = getPathForDisplay(change);
	const diff = normalizeLineEndings(change.diff || "");
	const stats = countDiffStats(diff);

	return {
		id: `${index}:${oldPath}:${newPath}`,
		path,
		oldPath,
		newPath,
		oldMode: change.a_mode || change.old_mode || "",
		newMode: change.b_mode || change.new_mode || "",
		newFile: Boolean(change.new_file || change.newFile),
		deletedFile: Boolean(change.deleted_file || change.deletedFile),
		renamedFile: Boolean(change.renamed_file || change.renamedFile),
		generatedFile: Boolean(change.generated_file || change.generatedFile),
		tooLarge: Boolean(change.too_large || change.tooLarge),
		diff,
		hunks: buildUnifiedDiff(change, diff, oldPath, newPath),
		diffBodies: getDiffBodies(diff),
		lineRows: parseDiffRows(diff),
		lang: getLangFromPath(path),
		additions: stats.additions,
		deletions: stats.deletions,
	};
};

const isOldSide = (side) => side === SPLIT_SIDE.OLD || side === "old";

const resolveLinePosition = (file, lineNumber, side) => {
	const line = Number(lineNumber);
	if (!Number.isFinite(line) || line < 1) return null;

	const row = file.lineRows.find((candidate) =>
		isOldSide(side) ? candidate.oldLine === line : candidate.newLine === line,
	);

	if (!row) return null;

	if (row.type === "add") return { newLine: row.newLine };
	if (row.type === "delete") return { oldLine: row.oldLine };
	return { oldLine: row.oldLine, newLine: row.newLine };
};

export const useMergeRequestDiff = (mr, enabled = true) => {
	const { user } = useAuth();
	const abortRef = useRef(null);
	const commentsAbortRef = useRef(null);
	const approvalAbortRef = useRef(null);
	const fileContentInFlightRef = useRef(new Map());
	const projectPath = getProjectPath(mr);
	const projectId = useMemo(
		() => (projectPath ? encodeProjectPath(projectPath) : ""),
		[projectPath],
	);
	const mrWebUrl = mr?.webUrl || "";
	const currentUsername =
		user?.profile?.username ||
		user?.profile?.preferred_username ||
		user?.profile?.nickname ||
		"";
	const iid = mr?.iid;
	const cacheKey = useMemo(
		() => getCacheKey({ projectId, iid, user }),
		[iid, projectId, user],
	);
	const [state, setState] = useState(initialState);
	const [approvalState, setApprovalState] = useState(() =>
		getInitialApprovalState(mr),
	);
	const [approvalAction, setApprovalAction] = useState(null);
	const [reactingEmojiKey, setReactingEmojiKey] = useState(null);

	const loadFileContent = useCallback(
		async (file) => {
			if (!file?.id || !projectId || !state.diffRefs || !user?.access_token) {
				return;
			}
			if (file.rawContentLoaded || file.rawContentLoading) return;

			const existing = fileContentInFlightRef.current.get(file.id);
			if (existing) return existing;

			const controller = new AbortController();
			const promise = (async () => {
				setState((prev) => ({
					...prev,
					files: prev.files.map((candidate) =>
						candidate.id === file.id
							? {
									...candidate,
									rawContentLoading: true,
									rawContentError: null,
								}
							: candidate,
					),
				}));

				try {
					const [oldContent, newContent] = await Promise.all([
						file.newFile
							? Promise.resolve("")
							: fetchRawFileContent({
									projectId,
									filePath: file.oldPath,
									ref: state.diffRefs.startSha,
									token: user.access_token,
									signal: controller.signal,
								}),
						file.deletedFile
							? Promise.resolve("")
							: fetchRawFileContent({
									projectId,
									filePath: file.newPath,
									ref: state.diffRefs.headSha,
									token: user.access_token,
									signal: controller.signal,
								}),
					]);

					if (controller.signal.aborted) return;
					setState((prev) => ({
						...prev,
						files: prev.files.map((candidate) =>
							candidate.id === file.id
								? {
										...candidate,
										oldContent,
										newContent,
										rawContentLoaded: true,
										rawContentLoading: false,
										rawContentError: null,
									}
								: candidate,
						),
					}));
				} catch (error) {
					if (controller.signal.aborted) return;
					setState((prev) => ({
						...prev,
						files: prev.files.map((candidate) =>
							candidate.id === file.id
								? {
										...candidate,
										rawContentLoaded: false,
										rawContentLoading: false,
										rawContentError:
											error.message || "Failed to load file context",
									}
								: candidate,
						),
					}));
				}
			})().finally(() => {
				fileContentInFlightRef.current.delete(file.id);
			});

			fileContentInFlightRef.current.set(file.id, promise);
			return promise;
		},
		[projectId, state.diffRefs, user?.access_token],
	);

	const reloadApprovalState = useCallback(
		async ({ silent = false, fallbackCurrentUserApproved = null } = {}) => {
			if (!enabled || !mr || !projectId || !iid || !user?.access_token) return;

			approvalAbortRef.current?.abort();
			const controller = new AbortController();
			approvalAbortRef.current = controller;

			if (!silent) {
				setApprovalState((prev) => ({
					...prev,
					loading: true,
					error: null,
				}));
			}

			try {
				const payload = await fetchApprovalState({
					projectId,
					iid,
					token: user.access_token,
					signal: controller.signal,
				});

				if (controller.signal.aborted) return;
				setApprovalState(
					normalizeApprovalState(payload, user, {
						currentUserApproved: fallbackCurrentUserApproved,
					}),
				);
			} catch (error) {
				if (controller.signal.aborted) return;
				setApprovalState((prev) => ({
					...prev,
					loading: false,
					error: error.message || "Failed to load approval status",
				}));
			}
		},
		[enabled, iid, mr, projectId, user],
	);

	const reloadComments = useCallback(
		async ({ force = false } = {}) => {
			if (!enabled || !mr || !projectId || !iid || !user?.access_token) return;

			if (!force) {
				const cachedState = getCachedDiffState(cacheKey);
				if (cachedState) {
					setState((prev) => ({
						...prev,
						discussions: cachedState.discussions,
						commentsLoading: false,
						commentsError: null,
					}));
					return;
				}
			}

			commentsAbortRef.current?.abort();
			const controller = new AbortController();
			commentsAbortRef.current = controller;
			setState((prev) => ({
				...prev,
				commentsLoading: true,
				commentsError: null,
			}));

			try {
				const discussions = await fetchDiscussions({
					projectId,
					iid,
					token: user.access_token,
					signal: controller.signal,
					currentUsername,
					mrWebUrl,
				});

				if (controller.signal.aborted) return;
				setState((prev) => {
					const next = {
						...prev,
						discussions,
						commentsLoading: false,
						commentsError: null,
					};
					if (prev.loaded && !prev.error) {
						setCachedDiffState(cacheKey, {
							...next,
							loading: false,
							commentsLoading: false,
						});
					}
					return next;
				});
			} catch (error) {
				if (controller.signal.aborted) return;
				setState((prev) => ({
					...prev,
					commentsLoading: false,
					commentsError: error.message || "Failed to load inline comments",
				}));
			}
		},
		[
			cacheKey,
			currentUsername,
			enabled,
			iid,
			mr,
			mrWebUrl,
			projectId,
			user?.access_token,
		],
	);

	const load = useCallback(
		async ({ force = false } = {}) => {
			if (!enabled || !mr) return;
			if (!projectId || !iid) {
				setState({
					...initialState,
					error:
						"Missing GitLab project path or merge request IID for diff loading",
					loaded: true,
				});
				return;
			}
			if (!user?.access_token) {
				setState({
					...initialState,
					error: "Missing GitLab access token for diff loading",
					loaded: true,
				});
				return;
			}

			if (!force) {
				const cachedState = getCachedDiffState(cacheKey);
				if (cachedState) {
					setState({
						...cachedState,
						loading: false,
						commentsLoading: false,
					});
					return;
				}
			} else {
				deleteCachedDiffState(cacheKey);
			}

			abortRef.current?.abort();
			const controller = new AbortController();
			abortRef.current = controller;
			setState((prev) => ({
				...prev,
				loading: true,
				commentsLoading: true,
				error: null,
				commentsError: null,
			}));

			try {
				const [diffs, diffRefs, discussionsResult] = await Promise.all([
					fetchDiffFiles({
						projectId,
						iid,
						token: user.access_token,
						signal: controller.signal,
					}),
					fetchDiffRefs({
						projectId,
						iid,
						token: user.access_token,
						signal: controller.signal,
					}).catch((error) => {
						console.warn("Failed to fetch GitLab diff refs:", error);
						return null;
					}),
					fetchDiscussions({
						projectId,
						iid,
						token: user.access_token,
						signal: controller.signal,
						currentUsername,
						mrWebUrl,
					})
						.then((discussions) => ({ discussions }))
						.catch((error) => ({ error })),
				]);

				if (controller.signal.aborted) return;
				if (discussionsResult.error) {
					console.warn(
						"Failed to fetch GitLab inline comments:",
						discussionsResult.error,
					);
				}

				const nextState = {
					files: diffs.map(normalizeDiffFile),
					diffRefs,
					discussions: discussionsResult.discussions || [],
					loading: false,
					commentsLoading: false,
					error: null,
					commentsError: discussionsResult.error?.message || null,
					loaded: true,
				};

				setState(nextState);
				if (!discussionsResult.error) {
					setCachedDiffState(cacheKey, nextState);
				}
			} catch (error) {
				if (controller.signal.aborted) return;
				setState((prev) => ({
					...prev,
					loading: false,
					commentsLoading: false,
					error: error.message || "Failed to load merge request diff",
					loaded: true,
				}));
			}
		},
		[
			cacheKey,
			currentUsername,
			enabled,
			iid,
			mr,
			mrWebUrl,
			projectId,
			user?.access_token,
		],
	);

	useEffect(() => {
		load();
		return () => {
			abortRef.current?.abort();
			commentsAbortRef.current?.abort();
			approvalAbortRef.current?.abort();
		};
	}, [load]);

	useEffect(() => {
		setApprovalState(getInitialApprovalState(mr));
	}, [mr]);

	useEffect(() => {
		reloadApprovalState();
		return () => {
			approvalAbortRef.current?.abort();
		};
	}, [reloadApprovalState]);

	const createCodeComment = useCallback(
		async ({ file, lineNumber, side, body }) => {
			if (!projectId || !iid || !user?.access_token) {
				throw new Error("Missing GitLab merge request context");
			}
			if (!state.diffRefs) {
				throw new Error(
					"GitLab diff refs are unavailable for this merge request",
				);
			}

			const positionLines = resolveLinePosition(file, lineNumber, side);
			if (!positionLines) {
				throw new Error("This line cannot be mapped to a GitLab diff position");
			}

			const position = {
				position_type: "text",
				base_sha: state.diffRefs.baseSha,
				start_sha: state.diffRefs.startSha,
				head_sha: state.diffRefs.headSha,
				old_path: file.oldPath,
				new_path: file.newPath,
				...(positionLines.oldLine ? { old_line: positionLines.oldLine } : {}),
				...(positionLines.newLine ? { new_line: positionLines.newLine } : {}),
			};

			const { data } = await gitlabRequest(
				`/projects/${projectId}/merge_requests/${iid}/discussions`,
				{
					token: user.access_token,
					method: "POST",
					body: {
						body,
						position,
					},
				},
			);

			await reloadComments({ force: true });
			return data;
		},
		[iid, projectId, reloadComments, state.diffRefs, user?.access_token],
	);

	const updateCodeComment = useCallback(
		async ({ discussionId, noteId, body }) => {
			if (!projectId || !iid || !user?.access_token) {
				throw new Error("Missing GitLab merge request context");
			}
			const trimmedBody = body?.trim();
			if (!discussionId || !noteId || !trimmedBody) {
				throw new Error("Missing GitLab comment details");
			}

			const { data } = await gitlabRequest(
				`/projects/${projectId}/merge_requests/${iid}/discussions/${discussionId}/notes/${noteId}`,
				{
					token: user.access_token,
					method: "PUT",
					body: { body: trimmedBody },
				},
			);

			await reloadComments({ force: true });
			return data;
		},
		[iid, projectId, reloadComments, user?.access_token],
	);

	const replyCodeComment = useCallback(
		async ({ discussionId, body }) => {
			if (!projectId || !iid || !user?.access_token) {
				throw new Error("Missing GitLab merge request context");
			}
			const trimmedBody = body?.trim();
			if (!discussionId || !trimmedBody) {
				throw new Error("Missing GitLab reply details");
			}

			const { data } = await gitlabRequest(
				`/projects/${projectId}/merge_requests/${iid}/discussions/${discussionId}/notes`,
				{
					token: user.access_token,
					method: "POST",
					body: { body: trimmedBody },
				},
			);

			await reloadComments({ force: true });
			return data;
		},
		[iid, projectId, reloadComments, user?.access_token],
	);

	const deleteCodeComment = useCallback(
		async ({ discussionId, noteId }) => {
			if (!projectId || !iid || !user?.access_token) {
				throw new Error("Missing GitLab merge request context");
			}
			if (!discussionId || !noteId) {
				throw new Error("Missing GitLab comment details");
			}

			await gitlabRequest(
				`/projects/${projectId}/merge_requests/${iid}/discussions/${discussionId}/notes/${noteId}`,
				{
					token: user.access_token,
					method: "DELETE",
				},
			);

			await reloadComments({ force: true });
		},
		[iid, projectId, reloadComments, user?.access_token],
	);

	const addCodeCommentReaction = useCallback(
		async ({ noteId, name }) => {
			if (!projectId || !iid || !user?.access_token) {
				throw new Error("Missing GitLab merge request context");
			}
			if (!noteId || !name) {
				throw new Error("Missing GitLab emoji reaction details");
			}

			const reactionKey = `${noteId}:${name}`;
			setReactingEmojiKey(reactionKey);
			try {
				const { data } = await gitlabRequest(
					`/projects/${projectId}/merge_requests/${iid}/notes/${encodeURIComponent(noteId)}/award_emoji?name=${encodeURIComponent(name)}`,
					{
						token: user.access_token,
						method: "POST",
					},
				);

				await reloadComments({ force: true });
				return data;
			} finally {
				setReactingEmojiKey(null);
			}
		},
		[iid, projectId, reloadComments, user?.access_token],
	);

	const approveMergeRequest = useCallback(async () => {
		if (!projectId || !iid || !user?.access_token) {
			throw new Error("Missing GitLab merge request context");
		}

		setApprovalAction("approve");
		setApprovalState((prev) => ({ ...prev, error: null }));
		try {
			const { data } = await gitlabRequest(
				`/projects/${projectId}/merge_requests/${iid}/approve`,
				{
					token: user.access_token,
					method: "POST",
					body: state.diffRefs?.headSha ? { sha: state.diffRefs.headSha } : {},
				},
			);

			setApprovalState(
				normalizeApprovalState(data, user, { currentUserApproved: true }),
			);
			deleteCachedDiffState(cacheKey);
			await reloadApprovalState({
				silent: true,
				fallbackCurrentUserApproved: true,
			});
			return data;
		} catch (error) {
			setApprovalState((prev) => ({
				...prev,
				error: error.message || "Failed to approve merge request",
			}));
			throw error;
		} finally {
			setApprovalAction(null);
		}
	}, [
		cacheKey,
		iid,
		projectId,
		reloadApprovalState,
		state.diffRefs?.headSha,
		user,
	]);

	const rejectMergeRequest = useCallback(async () => {
		if (!projectId || !iid || !user?.access_token) {
			throw new Error("Missing GitLab merge request context");
		}

		setApprovalAction("reject");
		setApprovalState((prev) => ({ ...prev, error: null }));
		try {
			const { data } = await gitlabRequest(
				`/projects/${projectId}/merge_requests/${iid}/unapprove`,
				{
					token: user.access_token,
					method: "POST",
				},
			);

			setApprovalState((prev) => ({
				...prev,
				currentUserApproved: false,
				loaded: true,
			}));
			deleteCachedDiffState(cacheKey);
			await reloadApprovalState({
				silent: true,
				fallbackCurrentUserApproved: false,
			});
			return data;
		} catch (error) {
			setApprovalState((prev) => ({
				...prev,
				error: error.message || "Failed to reject merge request",
			}));
			throw error;
		} finally {
			setApprovalAction(null);
		}
	}, [cacheKey, iid, projectId, reloadApprovalState, user]);

	return {
		...state,
		projectPath,
		diffRefs: state.diffRefs,
		approvalState,
		approvalAction,
		reload: () => load({ force: true }),
		reloadComments,
		reloadApprovalState,
		loadFileContent,
		canComment: Boolean(state.diffRefs),
		createCodeComment,
		updateCodeComment,
		deleteCodeComment,
		replyCodeComment,
		addCodeCommentReaction,
		reactingEmojiKey,
		approveMergeRequest,
		rejectMergeRequest,
	};
};
